import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input,
	OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { FertilizationDialogFields, FertilizationEventWarningTypes } from '../main';
import { IFormFertilizer } from '../../ranch-settings/modals/fertilizer/interfaces';
import { IFormSoilSample } from '../../soil-sample-event/interfaces';
import { PlantingService } from '../../planting-settings/service';
import { AppliedFertilizerChartService } from '../../applied-fertilizer-chart/applied-fertilizer-chart-service';
import { NBalanceChartService } from '../../applied-fertilizer-chart/n-balance-chart-service';
import { FertilizationEventForChart } from '../../planting-settings/interfaces';
import { DateUtility } from '../../../classes/dateUtility';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { NContributionDialog, NContributionDialogData } from './n-contribution-dialog';
import { IIrrigationMethod } from '../../../models/irrigation-method/interfaces';
import { SharedUpdateService } from '../../shared/dialogs/update.service';
import { NContributionUpdateService } from './n-contribution-update-service';
import { DIALOG_HIDE_CLASS } from '../../shared/constants';
import { FertilizationEvent } from '../fertilizationEvent';
import { IFertilizationEvent, nUnits } from '../interfaces';
import { BOKA } from '../../../classes/number';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { FertilizationRecommendationSummaryDialog, FertRecSummaryDialogForm } from './recommendation-summary-dialog';
import { MineralizationChartService } from '../../applied-fertilizer-chart/n-mineralization-chart-service';

export enum FertilizerPlannerTabs {
	N_BALANCE = 1,
	APPLIED = 2,
}

@Component({
	moduleId: module.id,
	selector: 'fertilization-event-planner',
	templateUrl: 'fertilization-event-planner.html',
	styleUrls: [ 'fertilization-event-planner.scss']
})

export class FertilizationEventPlannerComponent implements OnInit, OnDestroy {

	@ViewChild('chartContainer', { static: false }) private _chartContainer: ElementRef;
	@ViewChild('appliedChartContainer', { static: true }) private _appliedChartContainer: ElementRef;
	@ViewChild('mineralizationChartContainer', { static: false }) private _mineralizationChartContainer: ElementRef;

	@Input()
	public eventId: number;

	@Input()
	public maxDate: Date;

	@Input()
	public fertilizers: IFormFertilizer[];

	@Input()
	public isFertilizationsPerMonth: boolean;

	@Input()
	public isNDependent: boolean;

	@Input()
	public isTree: boolean;

	@Input()
	public soilSamples: IFormSoilSample[];

	@Input()
	public plantingId: number;

	@Input()
	public units: number;

	@Input()
	public FertilizationRecommendationLbsPerAcre: number;

	@Input()
	public NFertilizationRecommended: number;

	@Input()
	public isSaving: boolean;

	@Input()
	public formInitialized: Subject<FormGroup>;

	@Input()
	public nContributionForm: FormGroup;

	@Input()
	public irrigationMethods: IIrrigationMethod[];

	@Input()
	public warnings: FertilizationEventWarningTypes[];

	@Input()
	public NContribution: {
		UIVisible: boolean,
		calculatedPPM: number,
		recommendationHours: number,
		nextFertilizationDate: Date,
		canSaveWellsAsPlantingDefault?: boolean,
	}

	@Input()
	public lastUpdated: string;

	@Output()
	public onCancel: EventEmitter<void>;

	@Output()
	public onCreate: EventEmitter<void>;

	@Output()
	public onDelete: EventEmitter<void>;

	@Output()
	public onUpdate: EventEmitter<void>;

	@Output()
	public displayUI: EventEmitter<void>;

	@Output()
	public onDaysToNextFertilizationChanged: EventEmitter<void>;

	@Output()
	public onFertilizationsPerMonthChanged: EventEmitter<void>;



	public warningTypes = FertilizationEventWarningTypes;
	public nUnits = nUnits;
	public activeTab: FertilizerPlannerTabs;
	public form: FormGroup;
	public isLoaded = false;
	public isChartLoaded = false;
	public hasEstimatedNApplied = false;
	private _appliedFertilizerChartService: AppliedFertilizerChartService;
	private _nBalanceChartService: NBalanceChartService;
	private _mineralizationChartService: MineralizationChartService;
	protected _subscriptions$: Subject<boolean>;
	public isNContributionLoading = false;

	private _charts: {
		nBalance: Highcharts.ChartObject,
		applied: Highcharts.ChartObject,
		mineralization?: Highcharts.ChartObject
	};

	public get f(): { [key in FertilizationDialogFields]: AbstractControl } {
		if (!this.form) {
			return null
		};

		return this.form.controls as { [key in FertilizationDialogFields]: AbstractControl };
	}

	constructor(
		private _cd: ChangeDetectorRef,
		private _dialog: MatDialog,
		private _dialogRef: MatDialogRef<FertilizationEventPlannerComponent>,
		private _plantingService: PlantingService,
		private _nContributionUpdateService: NContributionUpdateService,
		private _sharedUpdateService: SharedUpdateService,
	) {
		this.onCancel = new EventEmitter<void>();
		this.onCreate = new EventEmitter<void>();
		this.onUpdate = new EventEmitter<void>();
		this.displayUI = new EventEmitter<void>();
		this.onDelete = new EventEmitter<void>();
		this.onDaysToNextFertilizationChanged = new EventEmitter<void>();
		this.onFertilizationsPerMonthChanged = new EventEmitter<void>();
		this.activeTab = FertilizerPlannerTabs.N_BALANCE;
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();

		this.formInitialized.pipe(takeUntil(this._subscriptions$)).subscribe((data) => {
			this.isLoaded = true;
			this.form = data;
			this._cd.detectChanges();
			this._nBalanceChartService = new NBalanceChartService(this._chartContainer);

			this._appliedFertilizerChartService =
				new AppliedFertilizerChartService(this._appliedChartContainer);

			this._mineralizationChartService =
				new MineralizationChartService(this._mineralizationChartContainer);

			this._valueChanges();
			this.fetchGraph();
		});
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public close(): void {
		this.onCancel.emit();
	}

	public toggleToFertUnits(): void {
		this.units = nUnits.FERTILIZER;
	}

	public toggleToLbsNPerAcre(): void {
		this.units = nUnits.LB_PER_ACRE;
	}

	/**
	 * Reflow highCharts inside tabs. Otherwise they create horizontal scrollbars
	 * @param $event
	 */
	public redrawGraph($event: MatTabChangeEvent): void {
		if (!this._charts || !this._charts.nBalance || !this._charts.applied) {
			return;
		}

		switch ($event.index) {
			case 0:
				this._charts.nBalance.reflow();
				break;
			case 1:
				this._charts.applied.reflow();
				break;
			case 2:
				if (this._charts.mineralization) {
					this._charts.mineralization.reflow();
				}
				break;
		}
	}


	private _createFertilizationEventForChart(): FertilizationEventForChart {
		let result: FertilizationEventForChart;

		if (!this.form || this.form.invalid) {
			return null;
		}

		result = {
			Id: this.f.Id.value,
			EventDate: DateUtility.ConvertToUTC(this.f.EventDate.value),
			NApplied: this.f.NApplied.value,
			ManagerN: this.f.managerNRecommendation.value,
			FertilizerApplied: this.f.FertilizerApplied.value,
			ManagerFertilizerAmount: this.f.ManagerFertilizerAmount.value,
			FertilizerId: this.f.FertilizerId.value,
			IsChemical: this.f.IsFertilizerChemical.value,
			DaysToNextFertilization: this._getDaysToNext()
		};

		return result;
	}

	public fetchGraph(): void {
		let fe: FertilizationEventForChart;

		this.isChartLoaded = false;

		fe = this._createFertilizationEventForChart();

		this._plantingService.getAppliedFertilizerChartCustom(this.plantingId, fe).then(response => {
			this._nBalanceChartService.convertToGraphData(response, this.f.EventDate.value,
				this._getDaysToNext());

			this.hasEstimatedNApplied = (response.EstimatedAppliedDaily || [])
				.some(x => x.Amount > 0);

			if (this.f.IsFertilizerChemical.value === false && !this.f.IsFertilizerPrivate.value) {
				if (!this._mineralizationChartService.chartContainer) {
					this._mineralizationChartService.chartContainer =
						this._mineralizationChartContainer;
				}

				this._mineralizationChartService.convertToGraphData(response);
			}

			this._appliedFertilizerChartService.convertToGraphData(response);

			this._charts = {
				nBalance: this._nBalanceChartService.drawChart(),
				applied: this._appliedFertilizerChartService.drawChart(),
				mineralization: this._mineralizationChartService.drawChart(),
			}

			this.isChartLoaded = true;
		});
	}

	private _getDaysToNext(): number {
		let result: number;

		if (this.f.IsFertilizerChemical.value === false) {
			return null;
		}

		if (this.isFertilizationsPerMonth) {
			return Math.floor(30 / this.f.FertilizationsPerMonth.value);
		} else {
			return this.f.DaysToNextFertilization.value;
		}
		return result;
	}

	private _appliedValueExists(): boolean {
		if (this.f.IsFertilizerChemical.value) {
			if (this.f.managerNRecommendation.value + this.f.NApplied.value > 0) {
				return true;
			} else {
				return false;
			}
		} else {
			if (this.f.ManagerFertilizerAmount.value + this.f.FertilizerApplied.value > 0) {
				return true;
			} else {
				return false;
			}
		}
	}

	private _valueChanges() {
		const DEBOUNCE_TIME = 1000;

		this.f.EventDate.valueChanges.pipe(
			filter(() => this.f.EventDate.dirty),
			debounceTime(DEBOUNCE_TIME),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid && this._appliedValueExists()) {
				this.fetchGraph();
			}
		});

		this.f.FertilizerId.valueChanges.pipe(
			filter(() => this.f.FertilizerId.dirty),
			debounceTime(DEBOUNCE_TIME),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid && this._appliedValueExists()) {
				this.fetchGraph();
			}
		});

		this.f.DaysToNextFertilization.valueChanges.pipe(
			filter(() => this.f.DaysToNextFertilization.dirty),
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged(),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid) {
				this.onDaysToNextFertilizationChanged.emit(); // refresh recommendation

				// updates the vertical next fertilzation date line
				if (this._appliedValueExists()) {
					this.fetchGraph();
				}
			}
		});

		this.f.FertilizationsPerMonth.valueChanges.pipe(
			filter(() => this.f.FertilizationsPerMonth.dirty),
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged(),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid) {
				this.onFertilizationsPerMonthChanged.emit();

				// updates the vertical next fertilzation date line
				if (this._appliedValueExists()) {
					this.fetchGraph();
				}
			}
		});

		this.f.FertilizerApplied.valueChanges.pipe(
			filter(() => this.f.FertilizerApplied.dirty),
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged(),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid && this._appliedValueExists()) {
				this.fetchGraph();
			}
		});

		this.f.NApplied.valueChanges.pipe(
			filter(() => this.f.NApplied.dirty),
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged(),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid && this._appliedValueExists()) {
				this.fetchGraph();
			}
		});

		this.f.managerNRecommendation.valueChanges.pipe(
			filter(() => this.f.managerNRecommendation.dirty),
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged(),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid && this._appliedValueExists()) {
				this.fetchGraph();
			}
		});

		this.f.ManagerFertilizerAmount.valueChanges.pipe(
			filter(() => this.f.ManagerFertilizerAmount.dirty),
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged(),
			takeUntil(this._subscriptions$)).subscribe(() => {

			if (this.form.valid && this._appliedValueExists()) {
				this.fetchGraph();
			}
		});
	}

	public onDeleteConfirmationOpen(): void {
		this.onDelete.emit();
	}

	public create(): void {
		this.onCreate.emit();
	}

	public update(): void {
		this.onUpdate.emit();
	}

	public openNContributionDialog(): void {
		let dialogConfig: MatDialogConfig;
		let params: NContributionDialogData;

		this.isNContributionLoading = true;
		this.displayUI.emit();

		this._nContributionUpdateService.loaded$.pipe(take(1), takeUntil(this._subscriptions$)).subscribe(() => {
			this.isNContributionLoading = false;

			params = {
				form: this.form,
				nContributionForm: this.nContributionForm,
				irrigationMethods: this.irrigationMethods,
				NContribution: this.NContribution,
				units: this.units,
				needsWells: this._nContributionUpdateService.needsWells,
				wellsForm: this._nContributionUpdateService.wellsForm
			}

			dialogConfig = {
				width: this._nContributionUpdateService.needsWells ? '700px' : '530px',
				autoFocus: false,
				disableClose: true,
				data: params
			};

			this._dialog.open(NContributionDialog, dialogConfig);
			this._dialogRef.addPanelClass(DIALOG_HIDE_CLASS);

			this._nContributionUpdateService.cancelled$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
				this._dialogRef.removePanelClass(DIALOG_HIDE_CLASS);
			});

			this._nContributionUpdateService.submitted$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
				this._dialogRef.removePanelClass(DIALOG_HIDE_CLASS);
			});
		});
	}

	public get shouldShowNContributionSummary(): boolean {
		return !!(this.isNDependent && this.f && this.f.NAppliedFromWater.value !== null);
	}

	public clearNContribution(): void {
		if (!this.form || !this.nContributionForm) {
			return;
		}

		this.f.NAppliedFromWater.setValue(null);
		this.f.IrrigationRecommendation.setValue(null);
		this.nContributionForm.get('UseWells').setValue(true);
		this.nContributionForm.get('PPMTotal').setValue(0);
		this.nContributionForm.get('IrrigationAmount').setValue(null);
		this.nContributionForm.get('IrrigationMethodId').setValue(null);
		this.nContributionForm.get('Ratio').setValue(0);
		this.nContributionForm.get('irrigationAmountHours').setValue(null);
		this.NContribution.calculatedPPM = 0;
		this.NContribution.recommendationHours = null;
		this.NContribution.nextFertilizationDate = null;
		this.NContribution.canSaveWellsAsPlantingDefault = false;

		if (this.form.valid && this._appliedValueExists()) {
			this.fetchGraph();
		}
	}

	public getNAppliedFromWaterInFertilizerUnits(): number {
		if (!this.f) {
			return null;
		}

		return BOKA.NumberUtil.roundToDecimalPlaces(FertilizationEvent.convertNToFertilizerAmount(
			this.f.NAppliedFromWater.value, this.form.value as IFertilizationEvent), 2);
	}

	public openRecommendationSummary(): void {
		let dialogConfig: MatDialogConfig;
		let params: FertRecSummaryDialogForm;

		params = {
			nInWater: this.getNAppliedFromWaterInFertilizerUnits(),
			form: this.form,
			plantingId: this.plantingId
		}

		dialogConfig = {
			width: '530px',
			autoFocus: false,
			disableClose: true,
			data: params
		};

		this._dialogRef.addPanelClass(DIALOG_HIDE_CLASS);
		this._dialog.open(FertilizationRecommendationSummaryDialog, dialogConfig);

		this._sharedUpdateService.cancel$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this._dialogRef.removePanelClass(DIALOG_HIDE_CLASS);
		});
	}
}
